import { AxiosError } from 'axios';
import { ISSUE_TYPES, PUBLISH_JIRA_LABEL, TASK_ID_PREFIX } from '../common/Constants';
import Strings from '../common/Strings';
import { Epic, JiraError, JiraProject, Story } from '../common/Types';
import { JiraCreateStoryBody } from '../services/JiraService/JiraApiClient';
import { markdownToAdf } from './jiraAdf';

const isTaskId = (id: string) => id.startsWith(`${TASK_ID_PREFIX}_`);

export const getCreateEpicsRequestBody = (epics: Epic[], currentProject: JiraProject) => {
  const issueUpdates = epics.map((epic) => {
    return {
      fields: {
        project: {
          id: currentProject.id
        },
        labels: [PUBLISH_JIRA_LABEL],
        summary: epic.summary,
        description: markdownToAdf(epic.description || ''),
        issuetype: {
          name: 'Epic'
        }
      }
    };
  });
  return { issueUpdates };
};

export const getCreateStoriesRequestBody = (
  stories: JiraCreateStoryBody[],
  currentProject: JiraProject
) => {
  const issueUpdates = stories.map((story) => {
    const parentData = story.jiraEpicId ? { parent: { id: story.jiraEpicId } } : {};
    const issueTypeName = isTaskId(story.id) ? ISSUE_TYPES.TASK : ISSUE_TYPES.STORY;
    return {
      fields: {
        ...parentData,
        project: {
          id: currentProject.id
        },
        labels: [PUBLISH_JIRA_LABEL],
        summary: story.summary,
        description: markdownToAdf(story.description || ''),
        issuetype: {
          name: issueTypeName
        }
      }
    };
  });
  return { issueUpdates };
};

export const isEpicsValid = (epics: Epic[]) => {
  return epics.every((epic) => epic.summary);
};

export const isStoriesValid = (stories: JiraCreateStoryBody[]) => {
  return stories.every((story) => !!story.summary && !!story.description);
};

export const throwExceptionIfIssueTypesInvalid = (
  epics: Epic[],
  stories: Story[],
  issueTypes: string[]
) => {
  const numOfEpics = epics.length;
  const numOfStories = stories.filter((story) => !isTaskId(story.id)).length;
  const numOfTasks = stories.filter((story) => isTaskId(story.id)).length;
  
  // Validate combined missing issue types
  if (
    numOfEpics > 0 &&
    numOfStories > 0 &&
    !issueTypes.includes(ISSUE_TYPES.EPIC) &&
    !issueTypes.includes(ISSUE_TYPES.STORY)
  ) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.EPIC_AND_STORY);
  }
  if (
    numOfEpics > 0 &&
    numOfTasks > 0 &&
    !issueTypes.includes(ISSUE_TYPES.EPIC) &&
    !issueTypes.includes(ISSUE_TYPES.TASK)
  ) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.EPIC_AND_TASK);
  }
  if (
    numOfStories > 0 &&
    numOfTasks > 0 &&
    !issueTypes.includes(ISSUE_TYPES.STORY) &&
    !issueTypes.includes(ISSUE_TYPES.TASK)
  ) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.STORY_AND_TASK);
  }
  
  // Validate individual missing issue types
  if (numOfEpics > 0 && !issueTypes.includes(ISSUE_TYPES.EPIC)) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.EPIC);
  }
  if (numOfStories > 0 && !issueTypes.includes(ISSUE_TYPES.STORY)) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.STORY);
  }
  if (numOfTasks > 0 && !issueTypes.includes(ISSUE_TYPES.TASK)) {
    throw new Error(Strings.ERROR_MESSAGES.ISSUE_TYPE_INVALID.TASK);
  }
  return true;
};

export const getPublishError = (error: unknown, items: Epic[] | Story[]) => {
  const err = error as AxiosError;
  const { status = 500, message = Strings.ERROR_MESSAGES.JIRA_PUBLISH_GENERIC_ERROR.BODY } = err;
  return items.map(
    (_, index) =>
      ({
        status,
        elementErrors: {
          errorMessages: [message],
          errors: err
        },
        failedElementNumber: index
      } as JiraError)
  );
};
